import { Box, Stack, Typography } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { Account } from "Components/Shared/Account/Account";
import { BlDivider } from "Components/Shared/BlDivider";
import { ButtonLink } from "Components/Shared/ButtonLink";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useIsTransferDisabled } from "Hooks/useIsTransferDisabled";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { Fragment } from "react";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { CompareArrows } from "@mui/icons-material";

const VISIBLE_ACCOUNT_STATUSES = [
  BankAccountStatus.AwaitingVerification,
  BankAccountStatus.Restricted,
  BankAccountStatus.Active,
  BankAccountStatus.PreApproved,
];

export const ConnectedAccounts = () => {
  const { data, isLoading, isError } = useBankingAccounts();
  const { data: userInfo } = useUserInfoQuery();
  const { isUserInMooneLite } = useIsUserInMooneLite();
  const { isTransferDisabled } = useIsTransferDisabled();
  const { t } = useResource();

  if (isLoading) {
    // Whether the widget renders at all is unknown until accounts resolve, so
    // a skeleton could flash and collapse into nothing for no-account users.
    return null;
  }

  if (!data || isError) {
    return (
      <Layout>
        <Typography color="error.main">
          {t(
            Resources.Pages.Content.Dashboard.BankAccountsWidget
              .ConnectionFailed,
          )}
        </Typography>
      </Layout>
    );
  }

  // The dashboards' setup checklists own the "connect a bank" prompt, so an
  // empty account list renders nothing here.
  if (data.items.length === 0) {
    return null;
  }

  const visibleAccounts = data.items.filter(({ status }) =>
    VISIBLE_ACCOUNT_STATUSES.includes(status),
  );

  if (visibleAccounts.length === 0) {
    return (
      <Layout>
        <Typography>
          {t(
            Resources.Pages.Content.Dashboard.BankAccountsWidget
              .NoActiveAccounts,
          )}
        </Typography>
      </Layout>
    );
  }

  const canTransferBetweenAccounts =
    visibleAccounts.filter(
      ({ status }) =>
        status === BankAccountStatus.Active ||
        status === BankAccountStatus.Restricted,
    ).length >= 2;

  return (
    <Layout>
      <Box display="flex" flexDirection="column" gap={2}>
        {visibleAccounts.map(({ bankAccountID, ...account }, index) => (
          <Fragment key={bankAccountID}>
            {index !== 0 && <BlDivider marginY={1} />}
            <Box
              component={Link}
              to={`${appUrl("bank-account-detail")}/${account.publicID}`}
              sx={{
                display: "flex",
                color: "inherit",
                textDecoration: "none",
                borderRadius: 1,
                cursor: "pointer",
                mx: -1,
                px: 1,
                py: 1,
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <Account
                {...account}
                balanceVariant={
                  account.isReceiveOnly || isUserInMooneLite
                    ? "hidden"
                    : userInfo?.isBalanceVisible === false
                      ? "blurred"
                      : "visible"
                }
                verificationStatusVisibility="intermediateOnly"
              />
            </Box>
          </Fragment>
        ))}
      </Box>
      {canTransferBetweenAccounts && (
        <ButtonLink
          link={appUrl("transfer-between-accounts")}
          text={t(Resources.Transfer.TransferButton)}
          color="primary"
          variant="contained"
          startIcon={<CompareArrows />}
          disabled={isTransferDisabled}
        />
      )}
    </Layout>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useResource();

  return (
    <PrimaryPaper sx={{ py: 4 }}>
      <Stack gap={4}>
        <PaperTitle>
          {t(Resources.Pages.Content.Dashboard.BankAccountsWidget.Title)}
        </PaperTitle>
        {children}
      </Stack>
    </PrimaryPaper>
  );
};
